import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import { CRMAccountStatus, CRMCustomerType, CRMUser, CRMUsersQueryParams, CursorPaginatedResponse } from "@swivl-mono/types";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";

// ============ Types ============

interface CacheEntry {
  data: CursorPaginatedResponse<CRMUser>;
  timestamp: number;
}

interface CRMUsersCache {
  [cacheKey: string]: CacheEntry;
}

interface StoreInterface {
  // Current view state
  currentData: CRMUser[];

  // Cursor pagination state
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  limit: number;

  // Filter state
  locationId?: number;
  groupId?: number;
  sort: string[];
  hasBalance?: boolean;
  hasAutopay?: boolean;
  status?: CRMAccountStatus[];
  tags?: string[];
  customerType?: CRMCustomerType[];
  search?: string;

  // Loading states
  isLoading: boolean;
  error?: string;

  // Cache
  _cache: CRMUsersCache;
}

// ============ Constants ============

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const DEFAULT_LIMIT = 100;

const BlankState: StoreInterface = {
  currentData: [],

  nextCursor: null,
  prevCursor: null,
  hasNext: false,
  hasPrev: false,
  limit: DEFAULT_LIMIT,

  locationId: undefined,
  groupId: undefined,
  sort: [],
  hasBalance: undefined,
  hasAutopay: undefined,
  status: undefined,
  tags: undefined,
  customerType: undefined,
  search: undefined,

  isLoading: false,
  error: undefined,

  _cache: {}
};

// ============ Helper Functions ============

function createCacheKey(params: CRMUsersQueryParams): string {
  const sortStr = params.sort?.slice().sort().join(",") || "";
  const cursor = params.after ?? params.before ?? "initial";
  const statusStr = params.status?.slice().sort().join(",") || "";
  const customerTypeStr = params.customerType?.slice().sort().join(",") || "";
  return `loc:${params.locationId ?? ""}|grp:${params.groupId ?? ""}|cursor:${cursor}|limit:${params.limit ?? DEFAULT_LIMIT}|srt:${sortStr}|bal:${params.hasBalance ?? ""}|auto:${params.hasAutopay ?? ""}|stat:${statusStr}|cust:${customerTypeStr}|srch:${params.search ?? ""}`;
}

function isCacheStale(entry: CacheEntry): boolean {
  return Date.now() - entry.timestamp > CACHE_TTL_MS;
}

// ============ Store ============

export default class CRMUsersModel {
  static useState = create<StoreInterface>(() => ({ ...BlankState }));
  static setState = CRMUsersModel.useState.setState;

  static get state() {
    return CRMUsersModel.useState.getState();
  }

  static reset() {
    CRMUsersModel.setState({ ...BlankState });
  }

  /**
   * Parse URL params and update state
   * Call this on component mount to sync state with URL
   */
  static initFromUrlParams(searchParams: URLSearchParams) {
    const locationId = searchParams.get("locationId");
    const groupId = searchParams.get("groupId");
    const sort = searchParams.get("sort");
    const limit = searchParams.get("limit");

    CRMUsersModel.setState({
      locationId: locationId ? Number(locationId) : undefined,
      groupId: groupId ? Number(groupId) : undefined,
      sort: sort ? sort.split(",") : [],
      limit: limit ? Number(limit) : DEFAULT_LIMIT
    });
  }

  /**
   * Get current query params from state for initial load (no cursor)
   */
  private static getInitialQueryParams(): CRMUsersQueryParams {
    const { locationId, groupId, limit, sort, hasAutopay, hasBalance, status, search, customerType, tags } = CRMUsersModel.state;
    return {
      locationId,
      groupId,
      limit,
      sort: sort.length > 0 ? sort : undefined,
      hasAutopay,
      hasBalance,
      status,
      search,
      customerType
      // tags
    };
  }

  /**
   * Try to get data from cache, returns null if not cached or stale
   */
  private static getFromCache(params: CRMUsersQueryParams): CursorPaginatedResponse<CRMUser> | null {
    const key = createCacheKey(params);
    const entry = CRMUsersModel.state._cache[key];

    if (!entry) {
      return null;
    }

    if (isCacheStale(entry)) {
      return null;
    }

    return entry.data;
  }

  /**
   * Store data in cache
   */
  private static setInCache(params: CRMUsersQueryParams, data: CursorPaginatedResponse<CRMUser>) {
    const key = createCacheKey(params);
    const newCache = {
      ...CRMUsersModel.state._cache,
      [key]: {
        data,
        timestamp: Date.now()
      }
    };
    CRMUsersModel.setState({ _cache: newCache });
  }

  /**
   * Invalidate all cache entries (call after mutations)
   */
  static invalidateCache() {
    CRMUsersModel.setState({ _cache: {} });
  }

  /**
   * Invalidate cache entries matching specific filters
   * Useful for targeted invalidation after mutations
   */
  static invalidateCacheForFilters(locationId?: number, groupId?: number) {
    const cache = { ...CRMUsersModel.state._cache };
    const keysToDelete: string[] = [];

    Object.keys(cache).forEach((key) => {
      const matchesLocation = locationId === undefined || key.includes(`loc:${locationId}|`);
      const matchesGroup = groupId === undefined || key.includes(`grp:${groupId}|`);

      if (matchesLocation && matchesGroup) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => delete cache[key]);
    CRMUsersModel.setState({ _cache: cache });
  }

  /**
   * Load CRM users with current filters (initial load, no cursor)
   * @param forceRefresh - Skip cache and fetch fresh data
   */
  static async load(forceRefresh: boolean = false) {
    const params = CRMUsersModel.getInitialQueryParams();

    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = CRMUsersModel.getFromCache(params);
      if (cached) {
        CRMUsersModel.setState({
          currentData: cached.data,
          nextCursor: cached.pagination.nextCursor,
          prevCursor: cached.pagination.prevCursor,
          hasNext: cached.pagination.hasNext,
          hasPrev: cached.pagination.hasPrev
        });
        return;
      }
    }

    // Fetch from API
    CRMUsersModel.setState({ isLoading: true, error: undefined });

    try {
      const response = await API.CRMUsers.getCRMUsers(Member.orgId!, params);

      // Store in cache
      CRMUsersModel.setInCache(params, response);

      // Update state
      CRMUsersModel.setState({
        currentData: response.data,
        nextCursor: response.pagination.nextCursor,
        prevCursor: response.pagination.prevCursor,
        hasNext: response.pagination.hasNext,
        hasPrev: response.pagination.hasPrev,
        isLoading: false
      });
    } catch (error) {
      warn("Error loading CRM users", error);
      CRMUsersModel.setState({
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to load users"
      });
    }
  }

  /**
   * Load with a specific cursor (used internally by nextPage/previousPage)
   */
  private static async loadWithCursor(cursor: string, direction: "after" | "before") {
    const { locationId, groupId, limit, sort, hasBalance, hasAutopay, status, search, customerType } = CRMUsersModel.state;

    const params: CRMUsersQueryParams = {
      locationId,
      groupId,
      limit,
      sort: sort.length > 0 ? sort : undefined,
      hasBalance,
      hasAutopay,
      status,
      search,
      customerType,
      [direction]: cursor
    };

    // Try cache first
    const cached = CRMUsersModel.getFromCache(params);
    if (cached) {
      CRMUsersModel.setState({
        currentData: cached.data,
        nextCursor: cached.pagination.nextCursor,
        prevCursor: cached.pagination.prevCursor,
        hasNext: cached.pagination.hasNext,
        hasPrev: cached.pagination.hasPrev
      });
      return;
    }

    // Fetch from API
    CRMUsersModel.setState({ isLoading: true, error: undefined });

    try {
      const response = await API.CRMUsers.getCRMUsers(Member.orgId!, params);

      // Store in cache
      CRMUsersModel.setInCache(params, response);

      // Update state
      CRMUsersModel.setState({
        currentData: response.data,
        nextCursor: response.pagination.nextCursor,
        prevCursor: response.pagination.prevCursor,
        hasNext: response.pagination.hasNext,
        hasPrev: response.pagination.hasPrev,
        isLoading: false
      });
    } catch (error) {
      warn("Error loading CRM users", error);
      CRMUsersModel.setState({
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to load users"
      });
    }
  }

  /**
   * Go to next page using the nextCursor
   */
  static async nextPage() {
    const { nextCursor, hasNext } = CRMUsersModel.state;

    if (hasNext && nextCursor) {
      await CRMUsersModel.loadWithCursor(nextCursor, "after");
    }
  }

  /**
   * Go to previous page using the prevCursor
   */
  static async previousPage() {
    const { prevCursor, hasPrev } = CRMUsersModel.state;

    if (hasPrev && prevCursor) {
      await CRMUsersModel.loadWithCursor(prevCursor, "before");
    }
  }

  /**
   * Set filters and reload from the beginning
   * Resets cursors when filters change
   */
  static async setFilters(filters: {
    locationId?: number;
    groupId?: number;
    sort?: string[];
    limit?: number;
    hasBalance?: boolean;
    hasAutopay?: boolean;
    status?: CRMAccountStatus[];
    search?: string;
    customerType?: CRMCustomerType[];
    // tags?: string[];
  }) {
    CRMUsersModel.setState((state) => ({
      locationId: "locationId" in filters ? filters.locationId : state.locationId,
      groupId: "groupId" in filters ? filters.groupId : state.groupId,
      sort: "sort" in filters ? filters.sort : state.sort,
      limit: "limit" in filters ? filters.limit : state.limit,
      hasBalance: "hasBalance" in filters ? filters.hasBalance : state.hasBalance,
      hasAutopay: "hasAutopay" in filters ? filters.hasAutopay : state.hasAutopay,
      status: "status" in filters ? filters.status : state.status,
      search: "search" in filters ? filters.search : state.search,
      customerType: "customerType" in filters ? filters.customerType : state.customerType,
      // tags: "tags" in filters ? filters.tags : state.tags,
      // Reset cursor state when filters change
      nextCursor: null,
      prevCursor: null,
      hasNext: false,
      hasPrev: false
    }));

    CRMUsersModel.invalidateCache();
    await CRMUsersModel.load();
  }

  /**
   * Set location filter and reload
   */
  static async setLocationFilter(locationId?: number) {
    await CRMUsersModel.setFilters({ ...CRMUsersModel.state, locationId });
  }

  /**
   * Set group filter and reload
   */
  static async setGroupFilter(groupId?: number) {
    await CRMUsersModel.setFilters({ ...CRMUsersModel.state, groupId });
  }

  /**
   * Set sort and reload
   */
  static async setSort(sort: string[]) {
    await CRMUsersModel.setFilters({ ...CRMUsersModel.state, sort });
  }

  /**
   * Set limit (page size) and reload from the beginning
   */
  static async setLimit(limit: number) {
    await CRMUsersModel.setFilters({ ...CRMUsersModel.state, limit });
  }

  /**
   * Check if there's a next page
   */
  static hasNextPage(): boolean {
    return CRMUsersModel.state.hasNext;
  }

  /**
   * Check if there's a previous page
   */
  static hasPreviousPage(): boolean {
    return CRMUsersModel.state.hasPrev;
  }

  /**
   * Prefetch adjacent pages for smoother navigation
   * Call this after loading a page to pre-warm the cache
   */
  static async prefetchAdjacentPages() {
    const { nextCursor, prevCursor, hasNext, hasPrev, locationId, groupId, limit, sort } = CRMUsersModel.state;

    const prefetchPromises: Promise<void>[] = [];

    // Prefetch next page if available
    if (hasNext && nextCursor) {
      const params: CRMUsersQueryParams = {
        locationId,
        groupId,
        limit,
        sort: sort.length > 0 ? sort : undefined,
        after: nextCursor
      };

      if (!CRMUsersModel.getFromCache(params)) {
        prefetchPromises.push(
          API.CRMUsers.getCRMUsers(Member.orgId!, params)
            .then((response) => {
              CRMUsersModel.setInCache(params, response);
            })
            .catch(() => {
              // Silently fail prefetch - not critical
            })
        );
      }
    }

    // Prefetch previous page if available
    if (hasPrev && prevCursor) {
      const params: CRMUsersQueryParams = {
        locationId,
        groupId,
        limit,
        sort: sort.length > 0 ? sort : undefined,
        before: prevCursor
      };

      if (!CRMUsersModel.getFromCache(params)) {
        prefetchPromises.push(
          API.CRMUsers.getCRMUsers(Member.orgId!, params)
            .then((response) => {
              CRMUsersModel.setInCache(params, response);
            })
            .catch(() => {
              // Silently fail prefetch - not critical
            })
        );
      }
    }

    // Don't await - let prefetch happen in background
  }

  static async createCRMUser(userData: { name: string; email: string; phoneNumber: string; locationId?: number }) {
    try {
      await API.CRMUsers.createCRMUser(Member.orgId!, userData);
      CRMUsersModel.invalidateCache();
      await CRMUsersModel.load();
    } catch (error) {
      warn("Error adding CRM user", error);
      throw error instanceof Error ? error : new Error("Failed to add user");
    }
  }
}
